import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

const AIAgentContext = createContext();

export const useAIAgent = () => useContext(AIAgentContext);

export const AIAgentProvider = ({ children }) => {
    // State: 'idle', 'selecting', 'confirming_job', 'analyzing', 'filling', 'input_needed', 'verifying', 'review', 'complete'
    const [agentState, setAgentState] = useState('idle');
    const [isActive, setIsActive] = useState(false);
    const [logs, setLogs] = useState([]);
    const [currentJob, setCurrentJob] = useState(null);
    const [pendingInput, setPendingInput] = useState(null); // { question, type: 'text'|'select', options?: [], onSubmit }
    const [agentMessage, setAgentMessage] = useState(null); // Current main message from agent

    const navigate = useNavigate();
    const location = useLocation();

    const addLog = useCallback((message, type = 'info') => {
        setLogs(prev => [...prev, { id: Date.now(), message, type, timestamp: new Date() }]);
    }, []);

    // Actions
    const activateAgent = useCallback(() => {
        setIsActive(true);
        setAgentState('selecting');
        addLog("AI Agent activated. Waiting for job selection.", 'system');
        setAgentMessage("Select a job posting to apply to with AI Agent mode. (Limit: 1 job per session)");
    }, [addLog]);

    const deactivateAgent = useCallback(() => {
        setIsActive(false);
        setAgentState('idle');
        setLogs([]);
        setCurrentJob(null);
        setAgentMessage(null);
        setPendingInput(null);
    }, []);

    const selectJobForAgent = useCallback((job) => {
        if (agentState !== 'selecting') return;

        setCurrentJob(job);
        setAgentState('confirming_job');
        setAgentMessage(`Starting AI Agent mode for ${job.title} at ${job.company?.name || 'this company'}. I'll guide you through this application step-by-step.`);
        addLog(`Selected job: ${job.title}`, 'action');
    }, [agentState, addLog]);

    const confirmJobStart = useCallback(() => {
        setAgentState('analyzing');
        addLog("Starting analysis phase...", 'system');
        setAgentMessage("I've analyzed the application. Found auto-fillable fields and sections requiring your input. Starting now...");
    }, [addLog]);

    const updateAgentState = useCallback((newState, message) => {
        setAgentState(newState);
        if (message) setAgentMessage(message);
        addLog(`State changed to: ${newState}`, 'debug');
    }, [addLog]);

    const cancelSession = useCallback(() => {
        addLog("Session cancelled by user.", 'warning');
        deactivateAgent();
    }, [addLog, deactivateAgent]);

    // Context value
    const value = {
        agentState,
        isActive,
        logs,
        currentJob,
        pendingInput,
        agentMessage,
        activateAgent,
        deactivateAgent,
        selectJobForAgent,
        confirmJobStart,
        updateAgentState,
        addLog,
        setPendingInput,
        cancelSession,
        setAgentMessage
    };

    return (
        <AIAgentContext.Provider value={value}>
            {children}
        </AIAgentContext.Provider>
    );
};
